import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, Award, Quote, ChevronRight, X, ChevronLeft } from "lucide-react";

const testimonials = [
  {
    name: "Tajudeen Ayola Faruk",
    text: "Studying here changed my life! The lecturers were supportive and inspiring.",
    photo: "/images/cartel1.jpg",
    rating: 5,
    course: "Computer Science",
    year: "Alumni",
    detailContent: `My experience at Federal Polytechnic Bida shaped both my academic foundation and my professional journey. Studying Computer Science exposed me to a learning environment where theory and practice were intentionally balanced, allowing me to build strong technical competence and critical thinking skills. The department's emphasis on hands-on work, project execution, and problem solving helped me develop confidence in applying classroom knowledge to real-world tasks. Beyond academics, the culture of discipline, teamwork, and innovation encouraged me to grow personally and professionally. The guidance from dedicated lecturers, access to supportive learning resources, and exposure to emerging technologies made my time at the institution truly transformative. I am proud to be a graduate of Federal Polytechnic Bida, and the values and skills I gained here continue to guide my career path.`,
  },
  {
    name: "Adeyemi Paul",
    text: "Amazing lecturers and a supportive environment. I felt at home instantly.",
    photo: "/images/pual.jpg",
    rating: 4,
    course: "Computer Science",
    year: "Alumni",
    detailContent: `My journey through Federal Polytechnic Bida was nothing short of life-shaping. Studying Computer Science there gave me a perfect mix of theory and real-world practice, helping me grow from curiosity into competence. The program's hands-on approach made learning exciting and meaningful — from coding projects to teamwork challenges that pushed my creativity and problem-solving skills.`,
  },
  {
    name: "Onumajuru Ikechukwu Jerimiah",
    text: "Amazing lecturers and a supportive environment. I felt at home instantly.",
    photo: "/images/ik.png",
    rating: 4,
    course: "Computer Science",
    year: "Alumni",
    detailContent: `I'm a Proud graduate of the Federal Polytechnic Bida, Computer Science Department. My time at Federal Polytechnic Bida was filled with growth, learning, and unforgettable experiences. Grateful for the knowledge gained, friendships built, and the strong foundation it gave me for the future. Forever a Bida alum.`,
  },
  {
    name: "Emmanuel Rumu Yusuf",
    text: "Studying here has been a transformative experience — both academically and personally.",
    photo: "/images/rumu.jpg",
    rating: 5,
    course: "Computer Science",
    year: "Alumni",
    detailContent: `My time at the Federal Polytechnic Bida has been nothing short of remarkable. The campus environment fosters creativity, innovation, and collaboration. From well-equipped computing labs to supportive faculty, every aspect of the learning process is designed to help students excel.

Through practical sessions, research projects, and seminars, I gained real-world exposure to cutting-edge technologies like Cyber Security, Artificial Intelligence, Data Mining, and Software Engineering. The Federal Polytechnic Bida's focus on both theory and hands-on application truly prepared me for professional challenges.

Beyond academics, the friendships, mentorship, and extracurricular activities shaped my character and leadership skills. Looking back, I can confidently say that my journey here laid the foundation for my success in technology and innovation.`
  },
  {
    name: "Comr. Emmanuel Joshua Mosugu",
    text: "A place that built my foundation for excellence...",
    photo: "/images/josh.jpg",
    rating: 4,
    course: "Computer Science",
    year: "Alumni",
    detailContent: `I am honored to be a graduate of Federal Polytechnic Bida, an institution renowned for its commitment to academic excellence and character development. The knowledge and values gained here continue to guide my professional and personal growth.`,
  },
];

const Testimonials = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showDetails, setShowDetails] = useState(false);
  const [selectedTestimonial, setSelectedTestimonial] = useState(null);

  const nextTestimonial = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const currentTestimonial = testimonials[currentIndex];

  return (
    <section className="relative w-full py-12 sm:py-16 md:py-20 bg-gradient-to-br from-slate-50 via-white to-blue-50 overflow-hidden">
      {/* Decorative Elements - Properly Contained */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-0 w-72 h-72 bg-yellow-200 rounded-full mix-blend-multiply blur-3xl opacity-20" />
        <div className="absolute bottom-0 right-0 w-72 h-72 bg-blue-200 rounded-full mix-blend-multiply blur-3xl opacity-20" />
      </div>

      <div className="relative w-full max-w-7xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <motion.div
          className="text-center mb-8 sm:mb-12 md:mb-16"
          initial={{ opacity: 0, y: -40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          viewport={{ once: true }}
        >
          <div className="inline-flex items-center px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold mb-5">
            <Award className="w-4 h-4 mr-2" />
            Student Voices
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold text-gray-900 mb-4 leading-tight px-4">
            What Our{" "}
            <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Students Say
            </span>
          </h2>
        </motion.div>

        {/* Main Testimonial Display */}
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center mb-8 sm:mb-12">
          {/* Student Photo Card */}
          <motion.div
            className="relative group w-full"
            initial={{ opacity: 0, x: -60 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            viewport={{ once: true }}
          >
            <div className="absolute -top-8 -left-8 w-36 h-36 bg-yellow-200 rounded-full opacity-20 group-hover:opacity-30 transition" />
            <div className="absolute -bottom-8 -right-8 w-28 h-28 bg-blue-200 rounded-full opacity-20 group-hover:opacity-30 transition" />

            <div className="relative bg-white/80 backdrop-blur-xl border border-gray-100 rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500 p-4 sm:p-6">
              <div className="relative overflow-hidden rounded-2xl">
                <img
                  src={currentTestimonial.photo}
                  alt={currentTestimonial.name}
                  className="w-full aspect-square object-cover rounded-2xl"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent rounded-2xl" />
              </div>

              <div className="mt-4 sm:mt-6 text-center">
                <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900">
                  {currentTestimonial.name}
                </h3>
                <p className="text-blue-600 font-semibold text-sm sm:text-base">
                  {currentTestimonial.course}
                </p>
                <p className="text-gray-500 text-xs sm:text-sm">{currentTestimonial.year}</p>
                
                <div className="flex justify-center mt-2 sm:mt-3 mb-3 sm:mb-4">
                  {[...Array(currentTestimonial.rating)].map((_, idx) => (
                    <Star key={idx} className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
              </div>
            </div>
          </motion.div>

          {/* Testimonial Content */}
          <motion.div
            className="relative w-full"
            initial={{ opacity: 0, x: 60 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            viewport={{ once: true }}
          >
            <Quote className="absolute -top-6 -left-6 w-10 h-10 sm:w-14 sm:h-14 text-blue-100" />
            <div className="relative z-10 bg-white rounded-3xl shadow-xl border border-gray-100 p-6 sm:p-8 md:p-10 hover:shadow-2xl transition">
              <blockquote className="text-sm sm:text-base md:text-lg text-gray-700 leading-relaxed mb-6 sm:mb-8 italic">
                "{currentTestimonial.text}"
              </blockquote>

              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-4 border-t border-gray-200">
                <div className="flex-1">
                  <p className="font-bold text-gray-900 text-sm sm:text-base">
                    {currentTestimonial.name}
                  </p>
                  <p className="text-gray-600 text-xs sm:text-sm">
                    {currentTestimonial.course} Student
                  </p>
                </div>
                <button
                  onClick={() => {
                    setSelectedTestimonial(currentTestimonial);
                    setShowDetails(true);
                  }}
                  className="inline-flex items-center justify-center px-4 sm:px-5 py-2 rounded-full bg-blue-50 text-blue-700 font-semibold text-sm hover:bg-blue-100 transition whitespace-nowrap"
                >
                  Read Full Story
                  <ChevronRight className="w-4 h-4 ml-1" />
                </button>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Navigation Controls */}
        <div className="flex justify-center items-center gap-3 sm:gap-4 mb-6 sm:mb-8">
          <button
            onClick={prevTestimonial}
            className="w-10 h-10 sm:w-12 sm:h-12 bg-white/80 backdrop-blur-xl border border-gray-100 rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110"
            aria-label="Previous testimonial"
          >
            <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
          </button>

          {/* Dots Navigation */}
          <div className="flex gap-2">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`h-2.5 sm:h-3 rounded-full transition-all duration-300 ${
                  index === currentIndex 
                    ? 'bg-blue-600 w-6 sm:w-8' 
                    : 'bg-gray-300 hover:bg-gray-400 w-2.5 sm:w-3'
                }`}
                aria-label={`Go to testimonial ${index + 1}`}
              />
            ))}
          </div>

          <button
            onClick={nextTestimonial}
            className="w-10 h-10 sm:w-12 sm:h-12 bg-white/80 backdrop-blur-xl border border-gray-100 rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110"
            aria-label="Next testimonial"
          >
            <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
          </button>
        </div>

        {/* Thumbnail Navigation */}
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-3 sm:gap-4 max-w-md sm:max-w-2xl mx-auto">
          {testimonials.map((testimonial, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={`relative overflow-hidden rounded-xl transition-all duration-300 ${
                index === currentIndex 
                  ? 'ring-2 sm:ring-4 ring-blue-500 scale-105 sm:scale-110' 
                  : 'opacity-60 hover:opacity-80'
              }`}
            >
              <img
                src={testimonial.photo}
                alt={testimonial.name}
                className="w-full h-16 sm:h-20 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
              <div className="absolute bottom-0.5 sm:bottom-1 left-0.5 sm:left-1 right-0.5 sm:right-1">
                <p className="text-white text-[10px] sm:text-xs font-semibold truncate">
                  {testimonial.name.split(' ')[0]}
                </p>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Details Modal */}
      <AnimatePresence>
        {showDetails && selectedTestimonial && (
          <motion.div
            className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowDetails(false)}
          >
            <motion.div
              className="relative bg-white rounded-3xl p-6 sm:p-8 w-full max-w-3xl max-h-[85vh] overflow-y-auto"
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.8 }}
              transition={{ duration: 0.3 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setShowDetails(false)}
                className="absolute top-4 right-4 w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center z-10"
              >
                <X className="w-5 h-5 text-gray-700" />
              </button>

              <div className="text-center mb-6">
                <img
                  src={selectedTestimonial.photo}
                  alt={selectedTestimonial.name}
                  className="w-16 h-16 sm:w-20 sm:h-20 mx-auto rounded-full object-cover border-4 border-yellow-400 shadow mb-4"
                />
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">
                  {selectedTestimonial.name}
                </h3>
                <p className="text-blue-600 font-semibold text-sm sm:text-base">
                  {selectedTestimonial.course}
                </p>
                <p className="text-gray-500 text-xs sm:text-sm">{selectedTestimonial.year}</p>
                
                <div className="flex justify-center mt-2 mb-4">
                  {[...Array(selectedTestimonial.rating)].map((_, idx) => (
                    <Star key={idx} className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
              </div>
              
              <p className="text-justify text-sm sm:text-base text-gray-700 leading-relaxed whitespace-pre-line">
                {selectedTestimonial.detailContent}
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Testimonials;